import React from 'react';

const ApprovalPrintThermalInternal = ({ approval, customer, reminder, termsAndConditions }) => {
  if (!approval) return null;

  const fmt = (d) => { if (!d) return 'N/A'; return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
  const cur = (a) => (parseFloat(a) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const calc = () => {
    const items = approval.items || [];
    const tw = items.reduce((s, i) => { const q = parseInt(i.quantity) || 1; return s + ((i.gross_wt ? parseFloat(i.gross_wt) : (parseFloat(i.weight_fixed) || 0)) * q); }, 0);
    const tfw = items.reduce((s, i) => { const q = parseInt(i.quantity) || 1; let nw = i.net_wt ? parseFloat(i.net_wt) * q : i.gross_wt ? parseFloat(i.gross_wt) * q : (parseFloat(i.weight_fixed) || 0) * q; return s + (nw * (parseFloat(i.purity) || 100) / 100); }, 0);
    const ta = (approval.advance_payments || []).reduce((s, p) => { const a = parseFloat(p.amount) || 0; return p.transaction_type === 'REFUND' ? s - a : s + a; }, 0);
    let ev = 0; if (approval.base_24k_rate) { ev = tfw * parseFloat(approval.base_24k_rate); if (approval.gst_applicable && approval.gst_input_style === 'EXCL') ev *= (1 + (approval.gst_percent || 3) / 100); }
    return { tw, tfw, ta, ev };
  };
  const t = calc();
  const bx = { border: '2px solid #000', padding: '2px 3px', marginBottom: '2px' };
  const hd = { fontSize: '13px', fontWeight: '900', borderBottom: '1px solid #000', paddingBottom: '1px', marginBottom: '2px', textTransform: 'uppercase' };
  const r = { fontSize: '12px', fontWeight: '700', marginBottom: '1px', lineHeight: '1.15' };
  const wn = { fontSize: '12px', color: '#dc2626', fontWeight: '900' };

  return (
    <div id="approval-thermal-internal-print-content" className="print-content-hidden">
      {approval?.has_provisional_changes && <div className="provisional-watermark">PROVISIONAL</div>}
      <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', lineHeight: '1.15', color: '#000', width: '74mm', padding: '1mm 2mm', margin: '0 auto', fontWeight: '700' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', borderBottom: '2px solid #000', paddingBottom: '2px', marginBottom: '2px' }}>
          <div style={{ fontSize: '14px', fontWeight: '900' }}>APPROVAL DETAILS</div>
          <div style={wn}>** INTERNAL **</div>
          <div style={{ fontSize: '13px', fontWeight: '900' }}>{approval.approval_id}</div>
          <div style={r}>Date: {fmt(approval.approval_date || approval.created_at)} | Status: <strong>{approval.status}</strong></div>
          {approval.created_by && <div style={r}>By: <strong>{approval.created_by}</strong></div>}
        </div>
        {/* Customer */}
        <div style={bx}>
          <div style={hd}>CUSTOMER</div>
          {customer && (<>
            <div style={{ fontSize: '13px', fontWeight: '900' }}>{customer.name}</div>
            {customer.mobile && <div style={r}>Mob: {customer.mobile}{customer.alternate_mobile ? ` / ${customer.alternate_mobile}` : ''}</div>}
            {customer.email && <div style={{ ...r, wordBreak: 'break-all' }}>Email: {customer.email}</div>}
            {customer.address && <div style={r}>Addr: {customer.address}</div>}
          </>)}
        </div>
        {/* Rate */}
        {approval.base_24k_rate && (
          <div style={bx}>
            <div style={hd}>RATE</div>
            <div style={r}>24K: Rs.{cur(approval.base_24k_rate)}/g{approval.gst_applicable === false ? ' | Kaccha (No GST)' : ` | GST: ${approval.gst_percent || 3}% ${approval.gst_input_style === 'INCL' ? '(Incl)' : '(Excl)'}`}</div>
          </div>
        )}
        {/* Items */}
        <div style={{ marginBottom: '2px' }}>
          <div style={hd}>ITEMS ({approval.items?.length || 0})</div>
          {approval.items?.map((item, idx) => (
            <div key={idx} style={{ ...bx, marginTop: '1px' }}>
              <div style={{ fontSize: '13px', fontWeight: '900' }}>#{idx + 1} {item.item_type}{item.tag_number ? ` [${item.tag_number}]` : ''}</div>
              {item.gross_wt ? (
                <div style={r}>Gross: {parseFloat(item.gross_wt).toFixed(3)}g | Less: {item.less_wt ? parseFloat(item.less_wt).toFixed(3) : '0.000'}g | <strong>Net: {item.net_wt ? parseFloat(item.net_wt).toFixed(3) : parseFloat(item.gross_wt).toFixed(3)}g</strong></div>
              ) : (
                <>{item.weight_fixed && <div style={r}>Wt: {item.weight_fixed}g</div>}{item.weight_type === 'Range' && <div style={r}>Wt: {item.weight_min}-{item.weight_max}g</div>}</>
              )}
              <div style={r}>{item.purity ? `Purity: ${item.purity}` : ''}{item.quantity ? ` | Qty: ${item.quantity}` : ''}{item.size ? ` | Size: ${item.size}` : ''}</div>
              {item.public_remark && <div style={{ ...r, fontStyle: 'italic', fontSize: '11px' }}>Note: {item.public_remark}</div>}
              {item.internal_remark && <div style={{ ...wn, fontSize: '11px', marginBottom: '1px' }}>Int: {item.internal_remark}</div>}
              {item.photos && item.photos.length > 0 && (
                <div style={{ marginTop: '2px' }}>
                  {item.photos.slice(0, 4).map((photo, pi) => (
                    <img key={pi} src={photo} alt={`${idx+1}-${pi+1}`} style={{ width: '70%', maxWidth: '50mm', height: 'auto', display: 'block', margin: '0 auto 1px auto', border: '1px solid #000' }} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Summary */}
        <div style={bx}>
          <div style={hd}>SUMMARY</div>
          <div style={r}>Items: {approval.items?.length || 0} | Weight: {t.tw.toFixed(2)}g | Fine: {t.tfw.toFixed(2)}g</div>
          {approval.base_24k_rate && <div style={{ ...r, fontWeight: '900' }}>Est. Value: Rs.{cur(t.ev)}</div>}
        </div>
        {/* Payments */}
        {approval.advance_payments && approval.advance_payments.length > 0 && (
          <div style={bx}>
            <div style={hd}>PAYMENTS</div>
            {(() => {
              const byMode = {};
              (approval.advance_payments || []).forEach(p => {
                const mode = p.mode || p.payment_type || p.payment_mode || 'Cash';
                if (!byMode[mode]) byMode[mode] = [];
                byMode[mode].push(p);
              });
              return Object.keys(byMode).map((mode, mi) => (
                <div key={mi} style={{ marginBottom: '1px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', backgroundColor: '#000', color: '#fff', padding: '1px 3px', marginBottom: '1px' }}>{mode}</div>
                  {byMode[mode].map((pay, pi) => {
                    const amt = parseFloat(pay.amount) || 0;
                    const isRef = pay.transaction_type === 'REFUND';
                    return (
                      <div key={pi} style={{ border: '1px solid #000', padding: '2px', marginBottom: '1px' }}>
                        <div style={r}>{fmt(pay.date)} | {isRef ? '-' : ''}Rs.{cur(Math.abs(amt))}{isRef ? ' (REFUND)' : ''}</div>
                        {pay.card_last4 && <div style={r}>Card: **** {pay.card_last4}{pay.pos_ref ? ` | POS: ${pay.pos_ref}` : ''}</div>}
                        {pay.upi_id && <div style={r}>UPI: {pay.upi_id}</div>}
                        {pay.upi_ref && <div style={r}>UPI Ref: {pay.upi_ref}</div>}
                        {pay.bank_name && <div style={r}>Bank: {pay.bank_name}</div>}
                        {pay.utr_ref && <div style={r}>UTR: {pay.utr_ref}</div>}
                        {pay.cheque_no && <div style={r}>Cheque: {pay.cheque_no}{pay.cheque_bank ? ` (${pay.cheque_bank})` : ''}</div>}
                        {pay.payer_acct_last4 && <div style={r}>Acct: **** {pay.payer_acct_last4}</div>}
                        {pay.reference && !pay.upi_ref && !pay.utr_ref && <div style={r}>Ref: {pay.reference}</div>}
                        {pay.items && pay.items.length > 0 && pay.items.map((item, ii) => (
                          <div key={ii} style={{ borderTop: '1px dotted #000', paddingTop: '1px', marginTop: '1px' }}>
                            {item.item_name && <div style={{ ...r, fontWeight: '900' }}>{item.item_name}</div>}
                            {item.gross_wt && <div style={r}>Gross: {item.gross_wt}g{item.less_wt ? ` | Less: ${item.less_wt}g` : ''}{item.net_wt ? ` | Net: ${item.net_wt}g` : ''}</div>}
                            {item.purity && <div style={r}>Purity: {item.purity}%{item.fine_wt ? ` | Fine: ${item.fine_wt}g` : ''}</div>}
                            {(item.purchase_rate || item.rate) && <div style={r}>Rate: Rs.{cur(item.purchase_rate || item.rate)}/g</div>}
                            {(item.line_total || item.value) && <div style={{ ...r, fontWeight: '900' }}>Value: Rs.{cur(item.line_total || item.value)}</div>}
                          </div>
                        ))}
                        {pay.deposits && pay.deposits.length > 0 && pay.deposits.map((dep, di) => (
                          <div key={di} style={{ borderTop: '1px dotted #000', paddingTop: '1px', marginTop: '1px' }}>
                            <div style={r}><strong>{dep.item_name || `Deposit ${di+1}`}</strong></div>
                            <div style={r}>Gross: {dep.gross_wt || 0}g | Net: {dep.net_wt || 0}g | Fine: {dep.fine_wt || 0}g</div>
                          </div>
                        ))}
                        {pay.fine_wt && !pay.deposits?.length && <div style={r}>Fine: {pay.fine_wt}g</div>}
                        {pay.note && <div style={{ ...r, fontSize: '11px', fontStyle: 'italic' }}>Note: {pay.note}</div>}
                      </div>
                    );
                  })}
                  <div style={{ fontSize: '12px', fontWeight: '900', textAlign: 'right', borderTop: '1px solid #000', paddingTop: '1px' }}>
                    {mode} Total: Rs.{cur(byMode[mode].reduce((s, p) => { const a = parseFloat(p.amount) || 0; return p.transaction_type === 'REFUND' ? s - a : s + a; }, 0))}
                  </div>
                </div>
              ));
            })()}
            <div style={{ fontWeight: '900', fontSize: '14px', borderTop: '3px solid #000', paddingTop: '2px', marginTop: '2px' }}>GRAND TOTAL: Rs.{cur(t.ta)}</div>
          </div>
        )}
        {/* Reminder */}
        {reminder && (
          <div style={bx}>
            <div style={hd}>REMINDER</div>
            <div style={r}>Due: {fmt(reminder.due_at)} | Status: {reminder.status}</div>
            {reminder.notes && <div style={r}>Notes: {reminder.notes}</div>}
          </div>
        )}
        {/* Signatures */}
        {(approval.management_signature || approval.customer_signature) && (
          <div style={bx}>
            <div style={hd}>SIGNATURES</div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {approval.management_signature && (
                <div><div style={{ fontSize: '11px' }}>Mgmt:</div><img src={approval.management_signature} alt="M" style={{ maxWidth: '80px', height: 'auto', maxHeight: '30px', border: '1px solid #000' }} />{approval.management_signature_name && <div style={{ fontSize: '10px' }}>{approval.management_signature_name}</div>}</div>
              )}
              {approval.customer_signature && (
                <div><div style={{ fontSize: '11px' }}>Customer:</div><img src={approval.customer_signature} alt="C" style={{ maxWidth: '80px', height: 'auto', maxHeight: '30px', border: '1px solid #000' }} />{customer?.name && <div style={{ fontSize: '10px' }}>{customer.name}</div>}</div>
              )}
            </div>
          </div>
        )}
        {/* Revisions */}
        {approval.revision_history && approval.revision_history.length > 0 && (
          <div style={bx}>
            <div style={hd}>REVISIONS ({approval.revision_history.length})</div>
            {approval.revision_history.map((rev, idx) => (
              <div key={idx} style={{ ...r, fontSize: '11px', borderBottom: '1px dotted #000', paddingBottom: '1px' }}>
                #{idx + 1} {fmt(rev.revised_at)} by {rev.revised_by}
                {rev.changes && rev.changes.length > 0 && (
                  <span> - {rev.changes.map((c, ci) => <span key={ci}>{typeof c === 'string' ? c : `${c.field || ''}: ${c.type || 'changed'}`}{ci < rev.changes.length - 1 ? ', ' : ''}</span>)}</span>
                )}
              </div>
            ))}
          </div>
        )}
        {/* Terms */}
        {termsAndConditions && (
          <div style={bx}>
            <div style={hd}>TERMS</div>
            <div style={{ fontSize: '10px', whiteSpace: 'pre-wrap', lineHeight: '1.1' }}>{termsAndConditions}</div>
          </div>
        )}
        {/* Footer */}
        <div style={{ textAlign: 'center', fontSize: '10px', borderTop: '2px solid #000', paddingTop: '1px', marginTop: '2px' }}>
          <div style={wn}>** CONFIDENTIAL **</div>
          <div style={{ color: '#666' }}>{new Date().toLocaleString('en-IN')}</div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalPrintThermalInternal;
